import { type QueryClient, QueryClientProvider } from '@tanstack/react-query';
import {
	createRootRouteWithContext,
	HeadContent,
	Outlet,
	Scripts,
} from '@tanstack/react-router';
import { createClientOnlyFn } from '@tanstack/react-start';
import { useEffect } from 'react';

import '../_shared/design-system/fonts.css';
import '../_shared/design-system/theme.css';

interface RouterContext {
	queryClient: QueryClient;
}

const ensureClientObservability = createClientOnlyFn(async () => {
	const { getClientObservability } = await import(
		'../_shared/observability/composition.client'
	);
	const obs = await getClientObservability();
	obs.log({ level: 'info', message: 'client observability ready' });
});

export const Route = createRootRouteWithContext<RouterContext>()({
	head: () => ({
		meta: [
			{ charSet: 'utf-8' },
			{ name: 'viewport', content: 'width=device-width, initial-scale=1' },
			{ title: 'Talent Promoter' },
			{ name: 'apple-mobile-web-app-title', content: 'Talent Promoter' },
		],
		links: [
			{ rel: 'icon', type: 'image/svg+xml', href: '/favicon.svg' },
			{
				rel: 'icon',
				type: 'image/png',
				sizes: '96x96',
				href: '/favicon-96x96.png',
			},
			{ rel: 'shortcut icon', href: '/favicon.ico' },
			{
				rel: 'apple-touch-icon',
				sizes: '180x180',
				href: '/apple-touch-icon.png',
			},
			{ rel: 'manifest', href: '/site.webmanifest' },
			// Preload the Space Grotesk weights used above the fold so the
			// browser fetches them in parallel with the HTML response rather
			// than discovering them after CSS parses. Eliminates the layout
			// shift that `font-display: swap` would otherwise produce when the
			// font arrives mid-paint. JetBrains Mono is unused here, so it
			// isn't preloaded.
			{
				rel: 'preload',
				as: 'font',
				type: 'font/woff2',
				href: '/fonts/space-grotesk-400.woff2',
				crossOrigin: 'anonymous',
			},
			{
				rel: 'preload',
				as: 'font',
				type: 'font/woff2',
				href: '/fonts/space-grotesk-500.woff2',
				crossOrigin: 'anonymous',
			},
			{
				rel: 'preload',
				as: 'font',
				type: 'font/woff2',
				href: '/fonts/space-grotesk-600.woff2',
				crossOrigin: 'anonymous',
			},
		],
	}),
	component: RootComponent,
});

function RootComponent() {
	// Source the client from router context — the same instance `getRouter()`
	// threads into every loader. On the server each request has its own client
	// (see `_shared/data/query-client.ts`); reading it here keeps the provider
	// in sync with the loaders' cache instead of minting a second client.
	const { queryClient } = Route.useRouteContext();

	useEffect(() => {
		void ensureClientObservability();
	}, []);

	return (
		<html lang='en'>
			<head>
				<HeadContent />
			</head>
			<body>
				<QueryClientProvider client={queryClient}>
					<Outlet />
				</QueryClientProvider>
				<Scripts />
			</body>
		</html>
	);
}
